// components/recipes/workflow/model.ts
//
// Production-accurate model shared by the pure WorkflowSentenceView and the
// store-backed WorkflowPanel container. All enum values / scaling / label
// mappings here MUST match the real recipe data model (verified against prod):
//   - numeric values are stored ×100 (percent: 1000 = 10%; money: cents)
//   - currency symbol comes from window.marketplace, not a hardcoded "$"
//   - state extra is the State enum int (Enabled=1 / Paused=2 / Archived=3)
//   - On Certain Days/Dates carry a nested sub-frequency (AfterEverySync | OncePerDay)
import { ActionsList, FrequenciesList, NumericalChangeActions } from '../../../util/contstants'
import { State } from '../../../constants'
import { getFrequencyDescription } from '../util'
import type { Day } from './DayOfWeekToggle'

export type Repeat = 'never' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly'
export type ValueSource = 'value' | 'variable'
export type NumericOp = string // NumericalChangeActions value, e.g. 'decrease-%'
export type ExecutionMode = 'after-sync' | 'scheduled' | 'manual'

/**
 * Capabilities supplied by the embedding route, rather than inferred from
 * recipe state. Clock schedules are supported only for private V2 segments.
 */
export interface WorkflowCapabilities {
    clockSchedulingAvailable: boolean
}

export const EXECUTION_MODES: { value: ExecutionMode; label: string; description: string }[] = [
    {
        value: 'after-sync',
        label: 'After every data sync',
        description: 'Run after each data refresh',
    },
    { value: 'scheduled', label: 'On a schedule', description: 'Run at selected local times' },
    { value: 'manual', label: 'Manually', description: 'Only when you run it' },
]

export const executionModesFor = ({
    clockSchedulingAvailable,
}: WorkflowCapabilities): typeof EXECUTION_MODES =>
    clockSchedulingAvailable
        ? EXECUTION_MODES
        : EXECUTION_MODES.filter((mode) => mode.value !== 'scheduled')

/**
 * Old daily/days/dates values stay untouched until the customer explicitly
 * selects a new execution mode.
 */
export const executionModeForRecipe = (
    frequency: number,
    hasClockSchedule: boolean,
    { clockSchedulingAvailable }: WorkflowCapabilities,
): ExecutionMode | null => {
    if (
        clockSchedulingAvailable &&
        (hasClockSchedule || frequency === FrequenciesList.Scheduled)
    ) return 'scheduled'
    if (frequency === FrequenciesList.AfterEveryDataSync) return 'after-sync'
    if (frequency === FrequenciesList.Manually) return 'manual'
    return null
}

// Friendly chip label for each real action. Source labels (Actions.tsx) are
// verbose ("Change Daily Budget"); the sentence wants short nouns.
export const ACTION_CHIP_LABEL: Record<number, string> = {
    [ActionsList.SetState]: 'State',
    [ActionsList.SetDailyBudget]: 'Daily budget',
    [ActionsList.SetDefaultBid]: 'Default bid',
    [ActionsList.SetBid]: 'Bid',
    [ActionsList.CreateExactMatchNegative]: 'Negate keyword',
    [ActionsList.ChangeBiddingStrategy]: 'Bidding strategy',
    [ActionsList.ChangeBidAdjustment]: 'Bid adjustment',
}

// Which actions carry a numeric value editor vs a state select vs nothing.
export const isNumericAction = (action: number) =>
    action === ActionsList.SetBid ||
    action === ActionsList.SetDefaultBid ||
    action === ActionsList.SetDailyBudget
export const isStateAction = (action: number) => action === ActionsList.SetState

// The 5 numeric ops, in the order the production HTMLSelect lists them.
export const NUMERIC_OPS: { value: NumericOp; verb: string; unit: '%' | '$' }[] = [
    { value: NumericalChangeActions.DecreasePercent, verb: 'Decrease', unit: '%' },
    { value: NumericalChangeActions.IncreasePercent, verb: 'Increase', unit: '%' },
    { value: NumericalChangeActions.DecreaseDelta, verb: 'Decrease', unit: '$' },
    { value: NumericalChangeActions.IncreaseDelta, verb: 'Increase', unit: '$' },
    { value: NumericalChangeActions.Set, verb: 'Set to', unit: '$' },
]

export const isPercentOp = (op: NumericOp) =>
    op === NumericalChangeActions.IncreasePercent || op === NumericalChangeActions.DecreasePercent

export const STATE_OPTIONS: { value: number; label: string; dot: string }[] = [
    { value: State.Enabled, label: 'Enabled', dot: 'bg-emerald-500' },
    { value: State.Paused, label: 'Paused', dot: 'bg-amber-500' },
    { value: State.Archived, label: 'Archived', dot: 'bg-gray-400' },
]

export const REPEAT_OPTIONS: { value: Repeat; label: string }[] = [
    { value: 'never', label: 'Does not repeat' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Every 2 weeks' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'yearly', label: 'Yearly' },
]

// ── Two independent axes: cadence (how often) + schedule restriction ────────
// Cadence = how often the automation evaluates. ("At a certain time" exists in
// the enum but is unused in data and intentionally not offered here.)
export const CADENCES: {
    value: number
    label: string
    description: string
    disabled?: boolean
}[] = [
    {
        value: FrequenciesList.AfterEveryDataSync,
        label: 'After every data sync',
        description: '4× per day',
    },
    { value: FrequenciesList.OncePerDay, label: 'Once per day', description: 'Every morning' },
    { value: FrequenciesList.Manually, label: 'Manually', description: 'Only when you run it' },
]

// Manually can't be restricted to a schedule (matches legacy: the sub-frequency
// radio only offered AfterEverySync / OncePerDay).
export const cadenceAllowsSchedule = (cadence: number) =>
    cadence === FrequenciesList.AfterEveryDataSync || cadence === FrequenciesList.OncePerDay

export type Restriction = 'none' | 'days' | 'dates'
export const RESTRICTIONS: { value: Restriction; label: string; description: string }[] = [
    { value: 'none', label: 'Every day', description: 'No schedule restriction' },
    { value: 'days', label: 'Certain days', description: 'Pick days of the week' },
    { value: 'dates', label: 'Certain dates', description: 'Pick a date + repeat' },
]

// Derive the (cadence, restriction) pair from a stored recipe's frequency fields.
export const readSchedule = (
    frequency: number,
    frequencyParams: any,
): { cadence: number; restriction: Restriction } => {
    if (frequency === FrequenciesList.OnCertainDays) {
        return {
            cadence: frequencyParams?.frequency ?? FrequenciesList.OncePerDay,
            restriction: 'days',
        }
    }
    if (frequency === FrequenciesList.OnCertainDates) {
        return {
            cadence: frequencyParams?.frequency ?? FrequenciesList.OncePerDay,
            restriction: 'dates',
        }
    }
    return { cadence: frequency, restriction: 'none' }
}

// ── Value formatting (×100 + currency) ──────────────────────────────────────
// Stored value is ×100. Percent 1000 -> "10%"; money 150 -> "$1.50".
export const formatStoredValue = (raw: number, op: NumericOp, currency: string): string => {
    const n = raw / 100
    if (isPercentOp(op)) {
        // Trim trailing zeros: 10.00 -> 10, 12.50 -> 12.5
        return `${parseFloat(n.toFixed(2))}%`
    }
    return `${currency}${n.toFixed(2)}`
}

// Chip text for the numeric value, e.g. "decrease by 10%", "set to $1.50",
// or "decrease by 'target_bid'" when sourced from a variable.
export const valueChipText = (
    op: NumericOp,
    source: ValueSource,
    rawValue: number,
    variableName: string,
    currency: string,
): string => {
    const meta = NUMERIC_OPS.find((o) => o.value === op)
    const verb = (meta?.verb ?? 'Change').toLowerCase()
    const amount =
        source === 'variable' ? varLabel(variableName) : formatStoredValue(rawValue, op, currency)
    return op === NumericalChangeActions.Set ? `set to ${amount}` : `${verb} by ${amount}`
}

// Recipe variables are stored with a leading "___" sentinel; show them clean.
export const prettyVar = (name: string): string => (name ?? '').replace(/^_+/, '')

// Variables display with a leading "$" sigil, e.g. "$target_bid".
export const varLabel = (name: string): string => `$${prettyVar(name)}`

// ── Summary line ────────────────────────────────────────────────────────────
// Display-only (not part of the saved payload). Reuses the production
// getFrequencyDescription for the days/dates wording so it reads identically.
export interface SummaryArgs {
    action: number
    op: NumericOp
    source: ValueSource
    rawValue: number
    variableName: string
    state: number
    currency: string
    cadence: number
    restriction: Restriction
    days: Day[]
    date: Date
    repeat: Repeat
}

export const buildSummary = (a: SummaryArgs): string => {
    const noun = (ACTION_CHIP_LABEL[a.action] ?? '').toLowerCase()
    let what = noun
    if (isNumericAction(a.action)) {
        what = `${noun} → ${valueChipText(a.op, a.source, a.rawValue, a.variableName, a.currency)}`
    } else if (isStateAction(a.action)) {
        what = `${noun} → ${(STATE_OPTIONS.find((s) => s.value === a.state)?.label ?? '').toLowerCase()}`
    }

    let when = CADENCES.find((c) => c.value === a.cadence)?.label.toLowerCase() ?? ''
    if (a.restriction === 'days') {
        when = getFrequencyDescription({ frequency: a.cadence, days: a.days })
    } else if (a.restriction === 'dates') {
        when = getFrequencyDescription({ frequency: a.cadence, date: a.date, repeat: a.repeat })
    }
    return `${what} · ${when}`
}

export type { Day }
